import { HttpErrorService } from '@/services/HttpError.service'
import { orderErrorHandbook } from '@/models/Errors'
import { HttpService } from '@/services/Http.service'

import type { Feedback, FeedbackInfo } from '@/models'

export class FeedbackService {
  private httpErrorService: HttpErrorService<keyof typeof orderErrorHandbook>
  private endpoint = '/feedback'
  private httpService: HttpService

  constructor(httpService: HttpService) {
    this.httpService = httpService
    this.httpErrorService = new HttpErrorService(orderErrorHandbook, 'order')
  }

  async create(feedback: Feedback) {
    const url = [this.endpoint, 'create'].join('/')

    return await this.httpService
      .post<FeedbackInfo>(url, {
        requestConfig: {
          cancellable: true,
          needCancelAtChangeRoute: true,
          forceRequest: true,
        },
        fetchConfig: {
          body: feedback,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async getFeedbackInfo(uuid: string) {
    const url = [this.endpoint, uuid].join('/')

    return await this.httpService
      .get<FeedbackInfo>(url, {
        requestConfig: {
          forceRequest: true,
          needGlobal404: false,
          cancellable: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async getUploadLink(uuid: string, file: File) {
    const url = [this.endpoint, uuid, 'get-upload-link'].join('/')

    return await this.httpService
      .post<{ url: string; fields: Record<string, string> }>(url, {
        requestConfig: {
          cancellable: true,
          needCancelAtChangeRoute: true,
          forceRequest: true,
        },
        fetchConfig: {
          body: {
            originalFileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async uploadFile(url: string, fields: Record<string, string>, file: File) {
    const formData = new FormData()

    Object.keys(fields).forEach((key) => {
      formData.set(key, fields[key])
    })

    formData.set('file', file)

    return await fetch(url, {
      method: 'POST',
      body: formData,
    })
  }

  async deleteFile(uuid: string, key: string) {
    const url = [this.endpoint, uuid, 'delete', 'image'].join('/')

    return await this.httpService
      .delete<string>(url, {
        requestConfig: {
          cancellable: true,
          needCancelAtChangeRoute: true,
          forceRequest: true,
        },
        fetchConfig: {
          query: {
            key,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async send(uuid: string) {
    const url = [this.endpoint, uuid, 'send'].join('/')

    return await this.httpService
      .post<FeedbackInfo>(url, {
        requestConfig: {
          cancellable: true,
          needCancelAtChangeRoute: true,
          forceRequest: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }
}
